import useDriversAutocomplete from '@/pages/Business/pages/Drivers/hooks/useDriversAutocomplete';
import type { Option } from '@/components/inputs/AutocompleteInput';
import { useAmendmentStorage } from './useAmendmentStorage';
import { useGetReasonSuggestionsQuery } from '../services';
import type { AmendmentTransition } from '../models';
import { useState, useMemo } from 'react';

export const REASON_MIN = 10;
export const REASON_MAX = 1000;

export type UseDriversAutocompleteHook = (props: {
  search?: string;
  minLength?: number;
}) => { driverOptions: Option[]; isDriversLoading: boolean };

interface UseAmendmentFormProps {
  amendmentOptions: Option[];
  onApply: (args: { transitionId: string; reason: string; driverId?: string }) => void;
  useDriversAutocompleteHook?: UseDriversAutocompleteHook;
}

export const useAmendmentForm = ({
  amendmentOptions,
  onApply,
  useDriversAutocompleteHook = useDriversAutocomplete,
}: UseAmendmentFormProps) => {

  const { draft, updateDraft } = useAmendmentStorage();

  const [amendment, setAmendmentState] = useState<Option | null>(
    () => (draft.amendment as Option) ?? null
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [reason, setReasonState] = useState<string>(
    () => (draft.reason as string) ?? ''
  );
  const [driverInput, setDriverInputState] = useState<string>(
    () => (draft.driverInput as string) ?? ''
  );
  const [driverSelection, setDriverSelectionState] = useState<Option | null>(
    () => (draft.driverSelection as Option) ?? null
  );

  const selectedAmendment: AmendmentTransition | null = amendment?.data as AmendmentTransition | null;
  const isDeliveredStatus: boolean = selectedAmendment?.toStatus?.name === 'DELIVERED';

  const { driverOptions, isDriversLoading } = useDriversAutocompleteHook({
    search: driverInput,
    minLength: 3,
  });

  const { data: reasonSuggestions = [], isFetching: isReasonSuggestionsLoading } =
    useGetReasonSuggestionsQuery(selectedAmendment?.id!, {
      skip: !selectedAmendment?.id,
    });

  const filteredOptions: Option[] = useMemo(() => {
    if (!searchQuery.trim()) return amendmentOptions;
    const reason: string = searchQuery.toLowerCase();
    return amendmentOptions.filter((option) => option.label.toLowerCase().includes(reason));
  }, [amendmentOptions, searchQuery]);

  const trimmedReasonLength: number = reason.trim().length;
  const reasonTouched: boolean = reason.length > 0;
  const reasonError: boolean = reasonTouched && trimmedReasonLength < REASON_MIN;
  const canApply: boolean =
    !!amendment &&
    trimmedReasonLength >= REASON_MIN &&
    reason.length <= REASON_MAX &&
    (!isDeliveredStatus || !!driverSelection);

  const handleAmendmentChange = (option: Option | null) => {
    setAmendmentState(option);
    setDriverSelectionState(null);
    setDriverInputState('');
    updateDraft({ amendment: option, driverSelection: null, driverInput: '' });
  };

  const handleReasonChange = (value: string) => {
    setReasonState(value);
    updateDraft({ reason: value });
  };

  const handleDriverChange = (option: Option | null) => {
    setDriverSelectionState(option);
    updateDraft({ driverSelection: option });
  };

  const handleDriverInputChange = (value: string) => {
    setDriverInputState(value);
    updateDraft({ driverInput: value });
  };

  const handleApply = () => {
    if (!canApply || !amendment) return;
    const transition = amendment.data as AmendmentTransition;
    onApply({
      transitionId: transition.id,
      reason: reason.trim(),
      ...(isDeliveredStatus && driverSelection && { driverId: driverSelection.data as string }),
    });
  };

  return {
    amendment,
    searchQuery,
    reason,
    driverInput,
    driverSelection,
    selectedAmendment,
    isDeliveredStatus,
    driverOptions,
    isDriversLoading,
    filteredOptions,
    reasonSuggestions,
    isReasonSuggestionsLoading,
    reasonError,
    canApply,
    handleAmendmentChange,
    handleReasonChange,
    handleDriverChange,
    handleDriverInputChange,
    setSearchQuery,
    handleApply,
  };
};
